import React, { ReactNode, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { IGif, INicheMap, IUserMap } from "@/common/services/types";
import { TileItem } from "./TileItem";
import { GifBlockPlaceholder } from "components/UI/Skeleton";
import { routes } from "@/common/utils/routes";
import { clsx } from "clsx";
import { useDeviceType } from "@/common/hooks/useDeviceType";
import { deviceTypeEnum } from "@/common/utils/device";
import { dateTimeHR } from "@/common/utils/dates";
import { GifTile } from "components/tagPage/GifTile";

import "./TileFeed.scss";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { EventSource } from "@/common/buses";
import { useGifStore } from "@/common/store/gifStore";
import { useUsersStore } from "@/common/store/storeUsers";
import { useNichesStore } from "@/common/store/storeNiches";

interface IProps {
  gifs: IGif[];
  niches?: INicheMap;
  hasMore: boolean;
  fetchMore: () => void;
  manage: boolean;
  onSelect?: (ids: string[]) => void;
  injector?: (pos: number, gif: IGif, fullScreen: boolean) => JSX.Element;
  rel?: string;
  order: string;
  verified?: boolean;
  isLoading?: boolean;
  isVerifyHidden?: boolean;
  userId?: string;
  isVerticalTiles?: boolean;
  users: IUserMap;
  header?: ReactNode;
  renderTileControls?: (gif: IGif) => ReactNode;
}

export const TileFeed = (props: IProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const isDesktop = useDeviceType() === deviceTypeEnum.DESKTOP;

  const updateGifs = useGifStore(state => state.updateGifs);
  const updateUsers = useUsersStore(state => state.updateUsers);
  const updateNiches = useNichesStore(state => state.updateNiches);

  useEffect(() => {
    updateGifs(props.gifs);
  }, [props.gifs]);

  useEffect(() => {
    if (props.niches) {
      updateNiches(Object.values(props.niches));
    }
  }, [props.niches]);

  useEffect(() => {
    updateUsers(Object.values(props.users));
  }, [props.users]);

  const onCheck = (id: string) => (): void => {
    const values = [...selected];
    const idx = values.indexOf(id);

    if (idx >= 0) {
      values.splice(idx, 1);
    } else {
      values.push(id);
    }

    setSelected(values);

    if (props.onSelect) {
      props.onSelect(values);
    }
  };

  const isChecked = (id: string): boolean => {
    return selected.indexOf(id) >= 0;
  };

  const visibleGifs = props.gifs.filter((gif) => !gif.cta);

  const eventSource = useAnalyticsStore((state) => state.eventSource);
  const sourceName = eventSource?.name ?? EventSource.Other;

  const renderTiles = () => {
    if (!visibleGifs.length && props.isLoading) {
      // eslint-disable-next-line no-magic-numbers
      return new Array(12).fill("").map((___, index) => (
        <React.Fragment key={index}>
          <GifBlockPlaceholder />
          {props.injector && props.injector(index, { id: "load" } as IGif, false)}
        </React.Fragment>
      ));
    }

    if (!visibleGifs.length && !props.isLoading) {
      return <p>No gifs to show on this page.</p>;
    }

    // todo: delete later (when all pages will use vertical tiles)
    if (props.isVerticalTiles) {
      return visibleGifs.map((gif: IGif, index) => (
        <React.Fragment key={gif.id}>
          <GifTile
            thumbnail={gif.urls.thumbnail || gif.urls.poster}
            imageAlt={`${gif.tags.slice(0, 3).join(", ")} porn ${gif.id} uploaded by ${gif.userName} on ${dateTimeHR(gif.createDate)}`}
            video={gif.type === 1 ? gif.urls.sd : undefined}
            verified={gif.verified && !props.isVerifyHidden}
            link={routes.watch(gif.id, props.rel, sourceName, props.order, props.verified)}
            views={gif.views}
            duration={gif.duration}
            gallery={!!gif.gallery}
            index={index}
            user={props.users[gif.userName]}
            isVertical
          />

          {props.injector && props.injector(index, gif, false)}
        </React.Fragment>
      ));
    }

    return visibleGifs.map((gif: IGif, index) => (
      <React.Fragment key={gif.id}>
        <TileItem
          thumbnail={gif.urls.thumbnail || gif.urls.poster}
          imageAlt={`${gif.tags.slice(0, 3).join(", ")} porn ${gif.id} uploaded by ${gif.userName} on ${dateTimeHR(gif.createDate)}`}
          video={gif.type === 1 ? gif.urls.sd : undefined}
          published={gif.published}
          verified={gif.verified && !props.isVerifyHidden}
          link={routes.watch(gif.id, props.rel, sourceName, props.order, props.verified)}
          views={gif.views}
          duration={gif.duration}
          gallery={!!gif.gallery}
          manage={props.manage}
          checked={isChecked(gif.id)}
          onCheck={onCheck(gif.id)}
          user={props.users[gif.userName]}
          index={index}
          controls={props.renderTileControls?.(gif)}
        />

        {props.injector && props.injector(index, gif, false)}
      </React.Fragment>
    ));
  };

  return (
    <InfiniteScroll
      dataLength={visibleGifs.length}
      hasMore={props.hasMore}
      next={props.fetchMore}
      scrollableTarget={!isDesktop ? "scrollableDiv" : undefined}
      loader={<></>}
    >
      {props.header}
      <div className={clsx("tileFeed", !props.isLoading && !visibleGifs.length && "isEmpty")}>
        {renderTiles()}
      </div>
    </InfiniteScroll>
  );
};
